import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { hrProfileService } from '@/lib/hrProfileService';
import { useEmployeeUser, buildSessionFromRows } from './EmployeeUserContext';
import type { InviteTokenResult } from '@/lib/hrProfileService';
import {
  Building2, Loader2, AlertCircle, CheckCircle2, User, Briefcase,
} from 'lucide-react';

export function EmployeeInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { session, seedSession } = useEmployeeUser();

  const [invite, setInvite] = useState<InviteTokenResult | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [linking, setLinking] = useState(false);

  // Load invite details
  useEffect(() => {
    if (!token) { setError('קישור לא תקין'); setLoadingInvite(false); return; }
    (async () => {
      const { data, error: err } = await hrProfileService.resolveInviteToken(token);
      if (err || !data) {
        setError('קישור ההזמנה לא נמצא או שפג תוקפו');
      } else {
        setInvite(data);
        if (data.status === 'active') setApproved(true);
      }
      setLoadingInvite(false);
    })();
  }, [token]);

  // After auth: link profile + load session if needed
  useEffect(() => {
    if (authLoading || !user || !invite || session || linking) return;
    (async () => {
      setLinking(true);
      // Link profile to user if not already linked
      await hrProfileService.linkProfileToUser(invite.profile_id, user.id);
      // Load full session
      const { data: rows } = await hrProfileService.getEmployeeSession();
      if (rows.length > 0) {
        const s = buildSessionFromRows(rows);
        if (s) seedSession(s);
      }
      setLinking(false);
    })();
  }, [authLoading, user, invite, session, linking, seedSession]);

  const handleLogin = () => {
    navigate('/employee', {
      state: { phone: invite?.phone, inviteToken: token },
    });
  };

  const handleApprove = async () => {
    if (!invite) return;
    setApproving(true);
    setError(null);
    const { error: err } = await hrProfileService.approveEmployment(invite.employment_id);
    setApproving(false);
    if (err) { setError(err); return; }
    setApproved(true);
    // Reload session to reflect approval
    const { data: rows } = await hrProfileService.getEmployeeSession();
    if (rows.length > 0) {
      const s = buildSessionFromRows(rows);
      if (s) seedSession(s);
    }
  };

  const goToPortal = () => navigate('/employee/portal', { replace: true });

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loadingInvite) {
    return (
      <div dir="rtl" className="min-h-screen bg-gradient-to-b from-teal-600 to-teal-400
                   flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-b from-teal-600 via-teal-500 to-teal-400
                 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-6 pt-8 pb-8 space-y-6">

          {error && !invite && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">שגיאה</h2>
              <p className="text-sm text-gray-500">{error}</p>
              <button onClick={() => navigate('/employee')}
                className="text-sm text-teal-600 font-medium hover:underline">
                חזור לדף הכניסה
              </button>
            </div>
          )}

          {invite && (
            <>
              {/* Company info */}
              <div className="text-center space-y-3">
                <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto">
                  <Building2 className="w-8 h-8 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">הזמנת הצטרפות</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    חברת <strong className="text-gray-900">{invite.company_name}</strong> מזמינה אותך להצטרף כעובד
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500">חברה:</span>
                  <span className="font-medium text-gray-900">{invite.company_name}</span>
                </div>
                {invite.job_title && (
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">תפקיד:</span>
                    <span className="font-medium text-gray-900">{invite.job_title}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500">ת.ז.:</span>
                  <span className="font-medium text-gray-900" dir="ltr">{invite.national_id}</span>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Not logged in — send to login */}
              {!user && !authLoading && (
                <>
                  <p className="text-center text-sm text-gray-500">
                    כדי לאשר את ההזמנה, עליך להירשם או להיכנס לחשבון שלך
                  </p>
                  <button onClick={handleLogin}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                               bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors">
                    הירשם / היכנס לחשבון
                  </button>
                </>
              )}

              {/* Logged in + linking */}
              {(authLoading || linking) && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
                </div>
              )}

              {/* Logged in — can approve */}
              {user && !authLoading && !linking && !approved && invite.status === 'pending_approval' && (
                <button onClick={handleApprove} disabled={approving}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                             bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm
                             disabled:opacity-50 transition-colors">
                  {approving
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <CheckCircle2 className="w-4 h-4" />
                  }
                  {approving ? 'מאשר...' : 'אשר הצטרפות'}
                </button>
              )}

              {/* Approved */}
              {approved && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 px-3.5 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span>ההצטרפות אושרה בהצלחה!</span>
                  </div>
                  {user && (
                    <button onClick={goToPortal}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                                 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors">
                      עבור לאזור האישי
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
